import React, { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { Dialog } from '@mui/material'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { toast } from 'react-toastify'
import * as estateDocumentsService from 'services/estateDocumentsService'
import { font, ink, line, radius, surface } from 'features/dashboard/tokens'

const EstateFileBuildingSharesModal = ({ estateId, file, canEdit, onChanged, onClose }) => {
  const [shares, setShares] = useState([])
  const [loading, setLoading] = useState(true)

  const load = () => {
    setLoading(true)
    return estateDocumentsService.listEstateFileBuildingShares({ estateId, fileId: file.id })
      .then(res => setShares(res.data?.data || []))
      .catch(() => toast.error('Could not load the Building Passports this document is in'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const remove = async (shareId) => {
    try {
      await estateDocumentsService.removeEstateFileBuildingShare({ estateId, fileId: file.id, shareId })
      await load()
      onChanged && onChanged()
    } catch (error) {
      toast.error('Could not remove the document from that Building Passport')
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth='xs' fullWidth>
      <Container>
        <Title>In {shares.length} Building Passport{shares.length === 1 ? '' : 's'}</Title>

        {loading ? (
          <SkeletonRows>
            {Array.from({ length: 3 }).map((_, index) => <SkeletonLine key={index} />)}
          </SkeletonRows>
        ) : shares.length === 0 ? (
          <Empty>Not added to any Building Passports.</Empty>
        ) : (
          <ShareList>
            {shares.map(s => (
              <ShareRow key={s.id}>
                <ApartmentOutlinedIcon style={{ fontSize: 18, color: ink.mute, flexShrink: 0 }} />
                <ShareName>{s.name || 'Building Passport'}</ShareName>
                {canEdit && (
                  <RowIconButton
                    type='button'
                    title='Remove from this Building Passport'
                    onClick={() => remove(s.id)}
                  >
                    <DeleteOutlineIcon style={{ fontSize: 16 }} />
                  </RowIconButton>
                )}
              </ShareRow>
            ))}
          </ShareList>
        )}

        <Footer>
          <CancelButton type='button' onClick={onClose}>Close</CancelButton>
        </Footer>
      </Container>
    </Dialog>
  )
}

export default EstateFileBuildingSharesModal

const skeletonPulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 1; }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 24px;
  background: ${surface.card};
`

const Title = styled.h2`
  margin: 0;
  font-size: ${font.heading};
  font-weight: 700;
  color: ${ink.base};
`

const Empty = styled.p`
  margin: 0;
  padding: 10px 0;
  font-size: ${font.body};
  color: ${ink.mute};
`

const ShareList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 320px;
  overflow-y: auto;
`

const ShareRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 4px;
  border-bottom: 1px solid ${line.base};

  &:last-child {
    border-bottom: none;
  }
`

const ShareName = styled.span`
  flex: 1;
  min-width: 0;
  font-size: ${font.body};
  color: ${ink.base};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RowIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border: none;
  border-radius: ${radius.xs};
  background: transparent;
  color: ${ink.mute};
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;

  &:hover {
    background: #fdecea;
    color: #b00020;
  }

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 1px;
  }
`

const SkeletonRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 0;
`

const SkeletonLine = styled.span`
  display: block;
  height: 16px;
  border-radius: ${radius.xs};
  background: #e7e9ee;
  animation: ${skeletonPulse} 1.3s ease-in-out infinite;
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

const CancelButton = styled.button`
  min-height: 40px;
  padding: 0 18px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.soft};
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  transition: color 160ms ease;

  &:hover {
    color: ${ink.base};
  }

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 1px;
  }
`
